import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View, ViewStyle } from 'react-native';

import { FavoriteSelected } from '@/assets/icons/favorite-selected';
import FavoriteUnselected from '@/assets/icons/favorite-unselected';
import { Button } from '@/components/ButtonComponent';
import { Text } from '@/components/TextComponent';
import { Box, Image, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { usePopup } from '@/feature/alerts/components/PopupsProvider';
import { DesktopActionButton } from '@/feature/desktop-nav/components/DesktopActionButton';
import { DesktopModal } from '@/feature/desktop-nav/components/DesktopModal';
import { useCasinoGameFavorite } from '@/feature/lobby-casino/hooks/use-casino-favorites';
import { common, withOpacity } from '@/styles/styles';

import { useCasinoGameInfo } from '../hooks/use-casino-game-info';
import { CasinoGame } from '../hooks/use-casino-games';
import { useStartCasinoGameUrl } from '../hooks/use-formatted-casino-game-url';

const GAME_INFO_POPUP_ID = 'casino-game-info';
const PANEL_WIDTH = 488;
const PANEL_HEIGHT = 640;
const THUMBNAIL_SIZE = 164;
const STAT_HEIGHT = 44;

const FADE_STOPS = [
    [0, 1],
    [0.2912, 0.93],
    [0.4456, 0.749],
    [0.5544, 0.503],
    [0.7088, 0.238],
    [1, 0],
];
const FADE_GRADIENT = `linear-gradient(0deg, ${FADE_STOPS.map(
    ([position, alpha]) => `${withOpacity(ds.colorValue.background.neutral.subtler, alpha)} ${position * 100}%`
).join(', ')})`;

export type CasinoGameInfoModalRef = {
    show: (game: CasinoGame) => void;
};

export const CasinoGameInfoModal = forwardRef<CasinoGameInfoModalRef, {}>((_, ref) => {
    const [game, setGame] = useState<CasinoGame | null>(null);
    const { openPopUp, closePopUp, isPopUpOpen } = usePopup();

    useImperativeHandle(ref, () => ({
        show: (selectedGame: CasinoGame) => {
            setGame(selectedGame);
            openPopUp(GAME_INFO_POPUP_ID);
        },
    }));

    const handleClose = () => closePopUp(GAME_INFO_POPUP_ID);

    return (
        <DesktopModal
            visible={isPopUpOpen(GAME_INFO_POPUP_ID)}
            onClose={handleClose}
            width={PANEL_WIDTH}
            height={PANEL_HEIGHT}
            borderRadius="r24"
        >
            {game ? <CasinoGameInfoPanel game={game} onClose={handleClose} /> : null}
        </DesktopModal>
    );
});

type CasinoGameInfoPanelProps = {
    game: CasinoGame;
    onClose: () => void;
};

const CasinoGameInfoPanel = ({ game, onClose }: CasinoGameInfoPanelProps) => {
    const { t } = useTranslation(['casino', 'common']);
    const { handleStartGame } = useStartCasinoGameUrl(game);
    const { betLimits, rtp, volatility } = useCasinoGameInfo(game);
    const imageUrl = game.thumbnail || game.logo;

    const handlePlayNow = () => {
        onClose();
        handleStartGame();
    };

    return (
        <>
            {game.backgroundImageUrl ? (
                <Image
                    source={{ uri: `https:${game.backgroundImageUrl}` }}
                    style={StyleSheet.absoluteFill}
                    resizeMode="cover"
                />
            ) : null}
            <Box style={StyleSheet.absoluteFill} backgroundColor={ds.colorToken.background.scrim} />
            <View style={styles.blur} />
            <View style={styles.fade} />

            <ScrollView style={common.flex} contentContainerStyle={styles.scrollContent}>
                <Box alignItems="center" gap="s16" py="s24">
                    {imageUrl ? (
                        <Box
                            width={THUMBNAIL_SIZE}
                            height={THUMBNAIL_SIZE}
                            borderColor={ds.colorToken.border.strong}
                            borderRadius="r36"
                            borderWidth={1}
                            overflow="hidden"
                            style={styles.thumbnailShadow}
                        >
                            <Image
                                source={{ uri: `https:${imageUrl}` }}
                                style={StyleSheet.absoluteFill}
                                resizeMode="cover"
                            />
                        </Box>
                    ) : null}
                    <Box alignItems="center">
                        <Text variant="headlineLarge" fontWeight="700" textAlign="center">
                            {game.name}
                        </Text>
                        {betLimits ? (
                            <Text variant="bodyMedium" color={ds.colorToken.text.secondary} textAlign="center">
                                {t('bet_limits', betLimits)}
                            </Text>
                        ) : null}
                    </Box>
                    {rtp || volatility ? (
                        <Row alignItems="center" justifyContent="center" gap="s32">
                            {rtp ? <GameStat label={t('rtp')} value={rtp} /> : null}
                            {rtp && volatility ? (
                                <Box width={1} height={STAT_HEIGHT} backgroundColor={ds.colorToken.border.strong} />
                            ) : null}
                            {volatility ? <GameStat label={t('volatility')} value={volatility} /> : null}
                        </Row>
                    ) : null}
                </Box>

                <Box gap="s12">
                    <FavoriteButton gameId={game.id} />
                    <Button size="l" hierarchy="primaryLilac" label={t('play_now')} onPress={handlePlayNow} />
                </Box>
            </ScrollView>

            <Box position="absolute" top={16} right={16}>
                <DesktopActionButton variant="close" onPress={onClose} accessibilityLabel={t('common:close')} />
            </Box>
        </>
    );
};

const GameStat = ({ label, value }: { label: string; value: string }) => (
    <Box alignItems="center">
        <Text variant="titleSmall" letterSpacing={-0.08} color={ds.colorToken.text.secondary}>
            {label}
        </Text>
        <Text variant="bodyMedium">{value}</Text>
    </Box>
);

const FavoriteButton = ({ gameId }: { gameId: string }) => {
    const { t } = useTranslation(['casino']);
    const { isFavorite, isUpdating, toggleFavorite } = useCasinoGameFavorite(gameId);
    const FavoriteIcon = isFavorite ? FavoriteSelected : FavoriteUnselected;

    return (
        <Button
            size="l"
            hierarchy="secondary"
            disabled={isUpdating}
            onPress={toggleFavorite}
            label={
                <Row alignItems="center" gap="s8">
                    <Text
                        variant="titleLarge"
                        letterSpacing={-0.43}
                        color={isUpdating ? ds.colorToken.text.disabled : ds.colorToken.text.primary}
                    >
                        {isFavorite ? t('added_to_favorites') : t('add_to_favorites')}
                    </Text>
                    <FavoriteIcon color={isUpdating ? ds.colorValue.icon.disabled : ds.colorValue.icon.primary} />
                </Row>
            }
        />
    );
};

const styles = StyleSheet.create({
    scrollContent: {
        flexGrow: 1,
        justifyContent: 'flex-end',
        padding: 24,
    },
    blur: {
        ...StyleSheet.absoluteFillObject,
        backdropFilter: 'blur(10px)',
        maskImage: FADE_GRADIENT,
    } as ViewStyle,
    fade: {
        ...StyleSheet.absoluteFillObject,
        backgroundImage: FADE_GRADIENT,
    } as ViewStyle,
    thumbnailShadow: {
        boxShadow: '0px 0px 50px 0px rgba(0, 0, 0, 0.6)',
    },
});
